/**
 * Sonda de alcance do backend (BUG-260) — alimenta o `connectivityStore`.
 *
 * Escuta `users/{uid}` com `includeMetadataChanges: true` e repassa só o
 * `fromCache` de cada snapshot. O SDK multiplexa listeners do mesmo documento
 * num único watch target, então isto NÃO custa uma segunda leitura de rede:
 * o perfil já está assinado pelo `AuthContext`; aqui só se olham os metadados
 * que aquele listener descarta.
 *
 * Por que uma sonda separada e não `includeMetadataChanges` no listener do
 * perfil: cada mudança de metadado (`hasPendingWrites` a cada write local,
 * `fromCache` a cada oscilação de rede) chegaria ao `setProfile` como um
 * objeto novo e re-renderizaria a árvore inteira sob o `AuthProvider`. A sonda
 * fala com um store fora do React e só quem lê a faixa re-renderiza.
 *
 * Erro no listener = sonda cega, não veredito: `resetBackendReachability`
 * apaga o que ela tinha dito. Faixa "instável" sem ninguém medindo nunca
 * apagaria.
 *
 * Renderiza nada; mora no `AppFrame` ao lado dos outros watchers.
 */
import { useEffect } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../lib/firebase/config";
import { useAuth } from "../../hooks/useAuth";
import {
  reportBackendSnapshot,
  resetBackendReachability,
} from "../../lib/net/connectivityStore";

export function BackendReachabilityProbe() {
  const { user, isAnonymous } = useAuth();
  const uid = user && !isAnonymous ? user.uid : null;

  useEffect(() => {
    if (!uid) return;
    const unsub = onSnapshot(
      doc(db, "users", uid),
      { includeMetadataChanges: true },
      (snap) => reportBackendSnapshot(snap.metadata.fromCache),
      () => resetBackendReachability(),
    );
    return () => {
      unsub();
      resetBackendReachability();
    };
  }, [uid]);

  return null;
}
